import { AsyncComponentDataStatus } from '../../state/types';
import { WidgetViewModelFactory } from '../viewModel';
import { formatLocalDateTimeToDateView } from '@wix/bookings-catalog-calendar-viewer-utils/dateAndTime';
import {
  createAgendaItemViewModel,
  createDummyAgendaItemViewModel,
} from './agendaItemViewModel/agendaItemViewModel';
import { ViewModelSubscription } from '../initViewModelSubscriptions';
import { ControllerFlowAPI } from '@wix/yoshi-flow-editor';
import { LanguageSettings } from '@wix/bookings-catalog-calendar-viewer-utils';

export type AgendaItemViewModel = {
  serviceName?: string;
  serviceImage?: { src: string; alt?: string };
  image?: any;
  startTime?: string;
  duration?: string;
  staffName?: string;
  staffImage?: string;
  location?: string;
  spots?: string;
  price?: string;
  buttonText?: string;
  disabled?: boolean;
  slotId: string;
};

export type DaySectionViewModel = {
  date: string;
  agendaItems: AgendaItemViewModel[];
};

export type AgendaViewModel = {
  days: DaySectionViewModel[];
  emptyState?: { title: string; subtitle: string };
};

export const createAgendaViewModel: WidgetViewModelFactory<
  AgendaViewModel | undefined
> = ({ state, flowAPI }) => {
  const { slots, services, languageSettings } = state;
  const {
    environment: { isEditor },
    translations: { t },
  } = flowAPI;
  if (
    slots.status === AsyncComponentDataStatus.LOADING ||
    slots.status === AsyncComponentDataStatus.ERROR ||
    services.status === AsyncComponentDataStatus.LOADING ||
    services.status === AsyncComponentDataStatus.ERROR
  ) {
    return;
  }

  if (
    isEditor &&
    slots.status === AsyncComponentDataStatus.IDLE &&
    !slots.data.length
  ) {
    return createDummyAgendaViewModel({ flowAPI, languageSettings });
  }

  const timeSlots = slots.data;
  if (!timeSlots || timeSlots.length === 0) {
    return {
      days: [],
      emptyState: {
        title: t('daily-agenda.errors.no-availability-main'),
        subtitle: t('daily-agenda.errors.no-availability-subtitle'),
      },
    };
  }

  const groupedSlotsByDays = new Map<string, AgendaItemViewModel[]>();
  timeSlots.forEach((slot) => {
    const formattedDate = formatLocalDateTimeToDateView(slot.localStartDate!);
    const agendaItem = createAgendaItemViewModel({
      slot,
      state,
      flowAPI,
    });

    if (groupedSlotsByDays.has(formattedDate)) {
      groupedSlotsByDays.get(formattedDate)?.push(agendaItem);
    } else {
      groupedSlotsByDays.set(formattedDate, [agendaItem]);
    }
  });
  const days = Array.from(groupedSlotsByDays.entries()).map(
    ([date, agendaItems]) => ({ date, agendaItems }),
  );
  return {
    days,
  };
};

export const subscribeToAgendaViewModel: ViewModelSubscription = ({
  subscribe,
  setViewModel,
  flowAPI,
  getState,
}) => {
  subscribe(
    (state) => ({
      slotsStatus: state.slots.status,
      servicesStatus: state.services.status,
      staffMembersStatus: state.staffMembers.status,
      slotsMetaDataStatus: state.slotsMetaData.status,
      bookedSlotsMetaDataStatus: state.bookedSlotsMetaData.status,
      bookedSlotsStatus: state.bookedSlots.status,
    }),
    () => {
      const agendaViewModel = createAgendaViewModel({
        state: getState(),
        flowAPI,
      });
      setViewModel({ agendaViewModel });
    },
  );
};

export const createDummyAgendaViewModel = ({
  flowAPI,
  languageSettings,
}: {
  flowAPI: ControllerFlowAPI;
  languageSettings: LanguageSettings;
}): AgendaViewModel => {
  const today = new Date();
  const tommorow = new Date(today);
  tommorow.setDate(today.getDate() + 1);
  return {
    days: [
      {
        date: formatLocalDateTimeToDateView(today.toISOString()),
        agendaItems: [
          createDummyAgendaItemViewModel({ flowAPI, languageSettings }),
          createDummyAgendaItemViewModel({
            flowAPI,
            languageSettings,
            overrideStartTime: '2025-04-07T10:00:00',
          }),
        ],
      },
      {
        date: formatLocalDateTimeToDateView(tommorow.toISOString()),
        agendaItems: [
          createDummyAgendaItemViewModel({ flowAPI, languageSettings }),
          createDummyAgendaItemViewModel({
            flowAPI,
            languageSettings,
            overrideStartTime: '2025-04-07T14:00:00',
          }),
        ],
      },
    ],
  };
};
